import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import type { AuthResponse, ProblemDetails } from './types';

export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  timeout: 15_000,
});

// Auth: připoj access token z Zustand store ke každému requestu
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ===== Cross-tab auth sync =====
// Refresh token se při každém refreshi rotuje (jednorázové použití). Když má uživatel
// otevřených víc karet, souběžné refreshe závodí: jedna rotuje cookie, druhé pak selže
// → odhlášení. Řešíme dvěma mechanismy:
//   1. navigator.locks — jen JEDNA karta refreshuje naráz (cross-tab serializace).
//   2. BroadcastChannel — výsledek (nová session / logout) se rozešle ostatním kartám.
const REFRESH_LOCK = 'ks-token-refresh';
const REFRESH_TIMEOUT_MS = 10_000;

const authChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('ks-auth') : null;

// Příchozí zprávy z jiných karet aplikujeme do lokálního store BEZ re-broadcastu (žádná smyčka).
if (authChannel) {
  authChannel.onmessage = (ev: MessageEvent) => {
    const msg = ev.data as { type: 'session'; payload: AuthResponse } | { type: 'logout' };
    if (msg?.type === 'session') {
      useAuthStore.getState().setSession(msg.payload.user, msg.payload.accessToken, msg.payload.accessTokenExpiresAt);
    } else if (msg?.type === 'logout') {
      useAuthStore.getState().clear();
    }
  };
}

/** Vrátí aktuální session, jen pokud access token ještě nějakou chvíli platí (jinak null). */
function currentValidSession(): AuthResponse | null {
  const s = useAuthStore.getState();
  if (s.accessToken && s.user && s.accessTokenExpiresAt) {
    const msLeft = new Date(s.accessTokenExpiresAt).getTime() - Date.now();
    if (msLeft > 5_000) {
      return { user: s.user, accessToken: s.accessToken, accessTokenExpiresAt: s.accessTokenExpiresAt };
    }
  }
  return null;
}

async function doRefresh(): Promise<AuthResponse | null> {
  try {
    const { data } = await axios.post<AuthResponse>('/api/v1/auth/refresh', null, {
      withCredentials: true,
      timeout: REFRESH_TIMEOUT_MS,
    });
    useAuthStore.getState().setSession(data.user, data.accessToken, data.accessTokenExpiresAt);
    authChannel?.postMessage({ type: 'session', payload: data });
    return data;
  } catch {
    useAuthStore.getState().clear();
    authChannel?.postMessage({ type: 'logout' });
    return null;
  }
}

// Auto-refresh při 401 (kromě endpointu refresh, aby nevznikla smyčka)
let refreshInFlight: Promise<AuthResponse | null> | null = null;

/**
 * Provede silent refresh access tokenu z HttpOnly cookie. Idempotentní v rámci karty
 * (in-flight dedup) i napříč kartami (navigator.locks + double-check). Reused boot guardem.
 */
export async function refreshOnce(): Promise<AuthResponse | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.locks) {
        return await navigator.locks.request(REFRESH_LOCK, async () => {
          // Double-check: než jsme získali lock, mohla refresh stihnout jiná karta
          // (a poslat nám novou session přes BroadcastChannel) — pak síť nevoláme.
          const fresh = currentValidSession();
          if (fresh) return fresh;
          return await doRefresh();
        });
      }
      return await doRefresh();
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError<ProblemDetails>) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/refresh') &&
      !original.url?.includes('/auth/login')
    ) {
      original._retry = true;
      const refreshed = await refreshOnce();
      if (refreshed) {
        if (original.headers) {
          original.headers.Authorization = `Bearer ${refreshed.accessToken}`;
        }
        return api(original);
      }
    }
    return Promise.reject(error);
  },
);

/** Hezčí helper na error message z ProblemDetails. */
export function errorMessage(err: unknown): string {
  if (axios.isAxiosError<ProblemDetails>(err)) {
    return err.response?.data?.detail ?? err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
